import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
    RouteConfigLoadEnd,
    RouteConfigLoadStart,
    Router,
    RouterEvent,
} from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

import { ControlWithErrors } from '@common/infrastructure/form-tools';

import { City, Prefecture } from '@lang/jp/model/japan-address';
import { JapanAddressService } from '@lang/jp/services/japan-address.service';
import { PhaseJpService } from '@lang/jp/services/phasejp.service';

import { ContentHelperService } from '../../services/contenthelper.service';
import { RegistrationService } from '../../services/registration.service';
import { EnJpContentService } from '../../services/enjp.content.service';
import { SHOW_LOCATION_SELECTORS } from '../../enjp.config';
import { EnjpCommunityService } from '../../services/community.service';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
    private destroyed$ = new Subject<void>();

    form: UntypedFormGroup;

    registerButtonClicked: boolean;
    isLoadingRegister: boolean;
    citySelectorReadonly = false;

    hero$: Observable<string> = of('#');
    heroMobile$: Observable<string> = of('#');
    interestedIndividualEnabled$ = of(false);

    infoBullets = [
        { id: 1, routerLink: '../info/how-much-can-i-save' },
        { id: 2, routerLink: '../info/switching-is-easy' },
        { id: 3, routerLink: '../info/increasing-renewable-energy' },
    ];

    showSelectors$: Observable<boolean> = of(false);
    prefectures$: Observable<Prefecture[]> = of([]);
    cities$: Observable<City[]> = of([]);
    reloadPrefectureById$ = new Subject<string>();

    get showVideo$(): Observable<boolean> {
        return this.contentService.valueExists('home-page-youtube-video');
    }

    get showSecondQuote$(): Observable<boolean> {
        return this.contentService.valueExists('home-second-quote-text');
    }

    get showSecondImage$(): Observable<boolean> {
        return this.contentService.valueExists('home-quote-image-2');
    }

    get prefecture(): ControlWithErrors {
        return this.form.get('prefecture');
    }
    get city(): ControlWithErrors {
        return this.form.get('city');
    }
    get isResidentialAreaSelected(): boolean {
        return (
            this.prefecture.value &&
            (this.citySelectorReadonly || this.city.value)
        );
    }

    get registrationEnabled$(): Observable<boolean> {
        return this.communityService.isCommunityRegistrationEnabled$;
    }

    constructor(
        public contentHelperService: ContentHelperService,
        public registrationService: RegistrationService,
        public contentService: EnJpContentService,
        public communityService: EnjpCommunityService,
        public phaseService: PhaseJpService,
        private router: Router,
        private fb: UntypedFormBuilder,
        private addressService: JapanAddressService,
        @Inject(SHOW_LOCATION_SELECTORS) private showLocationSelectors: boolean,
    ) {
        this.form = this.fb.group({
            prefecture: [null],
            city: [null],
        });

        this.prefectures$ = this.addressService.getPrefectures$();
        this.cities$ = this.addressService.getCities$();

        this.prefecture.valueChanges
            .pipe(
                takeUntil(this.destroyed$),
                debounceTime(300),
                distinctUntilChanged(),
            )
            .subscribe((p) => {
                const prefecture = p as Prefecture;
                const city = this.city?.value as City;

                //  Clear city value when it doesn't belong to prefecture
                if (!prefecture?.cities?.find((c) => c.id === city?.id)) {
                    this.city.setValue(null);
                }

                if (prefecture?.id === 'other') {
                    this.citySelectorReadonly = true;
                } else {
                    this.citySelectorReadonly = false;

                    if (prefecture?.cities) {
                        this.cities$ = of(prefecture.cities);
                    }
                }

                this.switchUrl();
            });

        this.city.valueChanges
            .pipe(
                takeUntil(this.destroyed$),
                debounceTime(300),
                distinctUntilChanged(),
            )
            .subscribe((_) => {
                this.switchUrl();
            });

        this.handleRegisterLazyLoadingSpinner();

        this.interestedIndividualEnabled$ =
            this.communityService.currentAuctionOpenForNewRegistrations$.pipe(
                takeUntil(this.destroyed$),
                map((isOpen) => !isOpen),
            );

        this.hero$ = this.contentService
            .value('hero-image')
            .pipe(takeUntil(this.destroyed$));

        this.heroMobile$ = this.contentService
            .value('hero-mobile-image')
            .pipe(takeUntil(this.destroyed$));

        this.reloadPrefectureById$
            .pipe(
                takeUntil(this.destroyed$),
                switchMap((prefectureId) =>
                    this.addressService.getFirstPrefectureById$(prefectureId),
                ),
            )
            .subscribe((prefecture) => {
                this.prefecture.setValue(prefecture);
            });
    }

    ngOnInit() {
        if (this.communityService?.communityCode) {
            if (this.showLocationSelectors) {
                this.showSelectors$ = this.addressService.showSelectors$(
                    this.communityService.communityCode,
                );
            }

            combineLatest([
                this.addressService.getFirstPrefectureByCommunityCode$(
                    this.communityService.communityCode,
                ),
                this.addressService.getFirstCityByCommunityCode$(
                    this.communityService.communityCode,
                ),
            ])
                .pipe(takeUntil(this.destroyed$))
                .subscribe(([prefecture, city]) => {
                    if (prefecture) {
                        this.prefecture.setValue(prefecture);
                    } else if (city) {
                        this.city.setValue(city);
                    }
                    this.persistSelections();
                });
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private handleRegisterLazyLoadingSpinner() {
        this.isLoadingRegister = false;
        this.registerButtonClicked = false;
        let asyncLoadCount = 0;

        this.router.events.subscribe((event): void => {
            if (
                event instanceof RouteConfigLoadStart &&
                event.route.path === 'register'
            ) {
                asyncLoadCount++;
            } else if (
                event instanceof RouteConfigLoadEnd &&
                event.route.path === 'register'
            ) {
                asyncLoadCount--;
            }
            this.isLoadingRegister = !!asyncLoadCount;
        });
    }

    private switchUrl() {
        const prefecture = this.prefecture?.value as Prefecture;
        const city = this.city?.value as City;

        let communityCode = this.communityService.communityCode;
        const isAlreadyCityCommunity = communityCode === city?.community?.code;

        if (!isAlreadyCityCommunity && prefecture?.community?.code) {
            communityCode = prefecture.community.code;
        }

        if (city) {
            if (city?.community?.code) {
                communityCode = city.community.code;
            }

            // Propagate prefecture value (when it is empty) from city
            if (!prefecture && city?.prefecture) {
                this.reloadPrefectureById$.next(city.prefecture.id);
            }
        }

        this.persistSelections();

        if (
            !communityCode ||
            communityCode === this.communityService.communityCode
        ) {
            return;
        }

        this.router.navigate([communityCode, 'home']);
    }

    private persistSelections(): void {
        const payload = { prefecture: null, city: null };

        const prefecture = this.prefecture?.value as Prefecture;
        const city = this.city?.value as City;

        if (prefecture) {
            payload.prefecture = prefecture.id;
        }

        if (city) {
            payload.city = city.id;
        }

        this.registrationService.homePageSelectors = payload;
    }
}
